import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Phone, ChatText, CurrencyDollar, ListBullets, Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API } from "@/lib/api";

export const QuickInteraction = ({ contactId, token, onDone }) => {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ type: "", notes: "", date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && types.length === 0) {
      axios.get(`${API}/comercial/interaction-types`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { setTypes(r.data || []); if (r.data?.length) setForm(f => ({ ...f, type: r.data[0].id })); })
        .catch(() => {});
    }
  }, [open, token, types.length]);

  const handleSubmit = async () => {
    if (!form.notes.trim()) { toast.error("Las notas son requeridas"); return; }
    setSaving(true);
    try {
      const iType = types.find(t => t.id === form.type);
      await axios.post(`${API}/comercial/${contactId}/interactions`, {
        type: form.type, type_name: iType?.name || form.type,
        notes: form.notes, date: form.date,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Interaccion registrada");
      setForm({ type: types[0]?.id || "", notes: "", date: new Date().toISOString().slice(0, 10) });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error("Error al registrar"); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-7 h-7 rounded flex items-center justify-center text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors"
          title="Registrar Interaccion"
          data-testid={`quick-interaction-${contactId}`}
        >
          <Phone size={15} weight="duotone" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Phone size={16} className="text-blue-500" /> Registrar Interaccion
        </h4>
        <div className="space-y-2">
          {types.length > 0 && (
            <div>
              <Label className="text-xs text-slate-500">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="input-sharp mt-1 h-8 text-xs" data-testid={`qi-type-${contactId}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                        {t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Fecha</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} className="input-sharp mt-1 h-8 text-xs" data-testid={`qi-date-${contactId}`} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Notas *</Label>
            <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input-sharp mt-1 min-h-[60px] text-xs" placeholder="Descripcion breve..." data-testid={`qi-notes-${contactId}`} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button size="sm" className="btn-primary h-7 text-xs" onClick={handleSubmit} disabled={saving || !form.notes.trim()} data-testid={`qi-submit-${contactId}`}>
              {saving ? "..." : "Registrar"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const QuickFollowUp = ({ contactId, token, onDone }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/comercial/${contactId}/comments`, { content: note }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Seguimiento agregado");
      setNote("");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error("Error al agregar seguimiento"); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-7 h-7 rounded flex items-center justify-center text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
          title="Agregar Seguimiento"
          data-testid={`quick-followup-${contactId}`}
        >
          <ChatText size={15} weight="duotone" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <ChatText size={16} className="text-emerald-500" /> Seguimiento Rapido
        </h4>
        <div className="space-y-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="input-sharp min-h-[70px] text-xs" placeholder="Nota de seguimiento..." data-testid={`qf-note-${contactId}`} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" onClick={handleSubmit} disabled={saving || !note.trim()} data-testid={`qf-submit-${contactId}`}>
              {saving ? "..." : "Agregar"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const QuickQuotations = ({ contactId, token, onViewQuotation }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && data === null) {
      setLoading(true);
      axios.get(`${API}/comercial/${contactId}/quotations`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setData(r.data || []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }
  }, [open, contactId, token, data]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setData(null); }}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-7 h-7 rounded flex items-center justify-center text-amber-500 hover:bg-amber-50 hover:text-amber-700 transition-colors"
          title="Historial de Cotizaciones"
          data-testid={`quick-quotations-${contactId}`}
        >
          <CurrencyDollar size={15} weight="duotone" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CurrencyDollar size={16} className="text-amber-500" /> Cotizaciones
        </h4>
        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4">Cargando...</p>
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Sin cotizaciones</p>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid={`qq-list-${contactId}`}>
            {data.map(q => {
              const colors = { borrador: "bg-slate-100 text-slate-600", pendiente: "bg-amber-100 text-amber-700", aprobada: "bg-green-100 text-green-700", rechazada: "bg-red-100 text-red-700" };
              return (
                <div key={q.id} className="flex items-center justify-between p-2 border border-slate-100 rounded hover:bg-slate-50" data-testid={`qq-row-${q.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs font-semibold text-blue-700">{q.quotation_number}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${colors[q.status] || "bg-slate-100"}`}>{q.status}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {format(new Date(q.created_at), "dd/MM/yyyy")} - ${(q.total_mxn || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN
                    </div>
                  </div>
                  {onViewQuotation && (
                    <button onClick={() => { setOpen(false); onViewQuotation(q.id); }} className="text-blue-500 hover:text-blue-700 p-1" data-testid={`qq-view-${q.id}`}>
                      <Eye size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const QuickTickets = ({ contactId, companyName, token }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && data === null) {
      setLoading(true);
      axios.get(`${API}/comercial/${contactId}/tickets`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setData(r.data || []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }
  }, [open, contactId, token, data]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setData(null); }}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-7 h-7 rounded flex items-center justify-center text-violet-500 hover:bg-violet-50 hover:text-violet-700 transition-colors"
          title="Historial de Tickets"
          data-testid={`quick-tickets-${contactId}`}
        >
          <ListBullets size={15} weight="duotone" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <ListBullets size={16} className="text-violet-500" /> Tickets - {companyName}
        </h4>
        {loading ? (
          <p className="text-xs text-slate-400 text-center py-4">Cargando...</p>
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">Sin tickets</p>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid={`qt-list-${contactId}`}>
            {data.map(t => {
              const statusColors = { abierto: "bg-blue-100 text-blue-700", en_progreso: "bg-amber-100 text-amber-700", cerrado: "bg-green-100 text-green-700", cancelado: "bg-red-100 text-red-700" };
              return (
                <div key={t.id} className="p-2 border border-slate-100 rounded hover:bg-slate-50" data-testid={`qt-row-${t.id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-mono text-xs font-semibold text-slate-800">{t.ticket_number}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${statusColors[t.status] || "bg-slate-100 text-slate-600"}`}>{t.status}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 leading-relaxed">
                    {t.hardware_type && <span className="mr-2">{t.hardware_type}</span>}
                    <span className="font-mono text-slate-400">{format(new Date(t.created_at), "dd/MM/yyyy")}</span>
                    {t.engineer_name && <span className="ml-2 text-slate-400">- {t.engineer_name}</span>}
                  </div>
                  {t.description && <p className="text-[10px] text-slate-400 truncate mt-0.5">{t.description}</p>}
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
